<div class="inputSearch h-full flex">
  <!-- Label -->
  <div class="flex items-center gap-2">
    <label [for]="id" [class]="'inputSearch__label ' + labelColor()">{{ label | transloco }}</label>
  </div>

  <!-- Saved addresses section (if user logged in) -->
  @if (isLogged()) {
    @if (loadingUserAddresses) {
      <!-- Skeleton mientras carga -->
      <div class="flex flex-col gap-1">
        <div class="saved-addresses-skeleton">
          <p-skeleton height="3.5rem" borderRadius="6px" styleClass="w-full" />
        </div>
        <small class="text-gray-300 dark:text-gray-500 text-xs block">
          {{ 'inputAddress.loadingSavedAddresses' | transloco }}
        </small>
      </div>
    } @else if (userAddresses.length > 0 || locationForm.disabled) {
      <!-- Select de direcciones (con ubicación actual si el formulario está bloqueado) -->
      <div>
        <p-floatlabel variant="in">
          <p-select
            [options]="savedAddressOptions"
            [(ngModel)]="selectedSavedAddress"
            optionLabel="label"
            [filter]="!locationForm.disabled"
            inputId="savedAddresses"
            fluid
            appendTo="body"
            filterBy="label,city,postal_code"
            size="large"
            (onChange)="onSavedAddressSelected($event.value)"
            [showClear]="!locationForm.disabled"
            [disabled]="locationForm.disabled"
            [attr.aria-readonly]="locationForm.disabled"
          >
            <ng-template let-address #item>
              <div class="flex flex-col gap-1">
                <span class="font-semibold">{{ address.label }}</span>
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  {{ address.city }}, {{ address.postal_code }}
                </span>
              </div>
            </ng-template>
          </p-select>
          @if (!locationForm.disabled || savedAddressesLabel) {
            <label for="savedAddresses">{{
              locationForm.disabled
                ? ('inputAddress.assignedAddressLabel' | transloco)
                : ('inputAddress.savedAddressesLabel' | transloco)
            }}</label>
          }
        </p-floatlabel>
      </div>
    } @else if (isPrimaryAddressField()) {
      <!--
        Usuario logueado sin direcciones guardadas en un campo editable (no fijo):
        en vez de dejar un hueco, se invita a registrar una dirección desde el
        mismo modal que se usa en "Mis direcciones". Reserva además la misma
        altura que ocupa el selector de direcciones guardadas para que esta
        columna quede alineada con la del campo opuesto (que sí suele mostrar
        ese selector, p. ej. por tener una dirección fija de destino).
      -->
      <button type="button" class="saved-addresses-cta" (click)="openAddAddressModal()">
        <i class="pi pi-plus-circle"></i>
        <span>{{ 'inputAddress.noSavedAddressesCta' | transloco }}</span>
      </button>
    } @else {
      <!--
        El campo opuesto (primario) ya muestra la invitación a registrar una
        dirección: no la repetimos como acción (una sola dirección sirve para
        ambos campos), pero mantenemos el mismo bloque deshabilitado en vez de
        dejarlo vacío, para que esta columna quede alineada con la del campo
        opuesto y no se vea "rota".
      -->
      <button
        type="button"
        class="saved-addresses-cta"
        disabled
        [pTooltip]="'inputAddress.noSavedAddressesSecondaryTooltip' | transloco"
        tooltipPosition="top"
      >
        <i class="pi pi-plus-circle"></i>
        <span>{{ 'inputAddress.noSavedAddressesCta' | transloco }}</span>
      </button>
    }
  }

  <!-- Unified search form -->
  <form [formGroup]="locationSearchForm" class="flex flex-col gap-4">
    <!-- Country field -->
    <div>
      <p-floatlabel variant="in">
        <p-select
          [options]="countriesSuggestions"
          formControlName="country"
          optionLabel="name"
          optionValue="code"
          [filter]="true"
          inputId="country"
          fluid
          appendTo="body"
          [virtualScroll]="true"
          [virtualScrollItemSize]="38"
          filterBy="name"
          size="large"
          [tabindex]="isCountryFixed ? -1 : 0"
          [class.country-fixed]="isCountryFixed"
          [attr.aria-readonly]="isCountryFixed ? true : null"
          (onFocus)="onAddressBlockFocus()"
        >
          <ng-template let-selectedOption #selectedItem>
            <div class="flex items-center gap-2">
              <img
                [ngSrc]="'/assets/flags/' + selectedOption.code.toLowerCase() + '.png'"
                [alt]="'inputAddress.flagAlt' | transloco: { name: selectedOption.name }"
                width="20"
                height="15"
                style="object-fit: cover"
              />
              <span class="text-wrap">
                {{ selectedOption.name }}
              </span>
            </div>
          </ng-template>
          <ng-template let-country #item>
            <div class="flex items-center gap-2">
              <img
                [ngSrc]="'/assets/flags/' + country.code.toLowerCase() + '.png'"
                [alt]="'inputAddress.flagAlt' | transloco: { name: country.name }"
                width="20"
                height="15"
                style="object-fit: cover"
              />
              <span class="text-wrap">
                {{ country.name }}
              </span>
            </div>
          </ng-template>
        </p-select>
        <label for="country">{{ 'inputAddress.countryLabel' | transloco }}</label>
      </p-floatlabel>
      <d2d-error-message [control]="locationSearchForm.get('country')" />
    </div>

    <!-- Smart query field: Postal Code or Address -->
    <div class="flex flex-col gap-2">
      <p-floatlabel variant="in">
        <p-autocomplete
          [suggestions]="smartSuggestions"
          formControlName="query"
          id="smart_query"
          autocomplete="off"
          [maxlength]="100"
          fluid
          appendTo="body"
          (completeMethod)="smartSearch($event)"
          (onSelect)="onLocationSelected($event)"
          [emptyMessage]="'inputAddress.noResultsFound' | transloco"
          [forceSelection]="false"
          [autoOptionFocus]="false"
          [group]="true"
          optionGroupLabel="label"
          optionGroupChildren="items"
          (onFocus)="onAddressBlockFocus()"
        >
          <ng-template let-group pTemplate="group">
            <div class="flex items-center gap-2">
              <i [class]="getGroupIcon(group.label)"></i>
              <span class="font-semibold">{{ group.label }}</span>
            </div>
          </ng-template>

          <ng-template let-item pTemplate="item">
            @if (item._type === 'v3') {
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <span class="font-semibold">{{ item.postal_code || item.city }}</span>
                  @if (item.postal_code && item.city) {
                    <span class="text-sm text-gray-600 dark:text-gray-400">
                      {{ item.city }}
                    </span>
                  }
                </div>
                @if (item.state) {
                  <span class="text-xs text-gray-500 dark:text-gray-500">
                    {{ item.state }}
                    @if (item.province && item.province !== item.state) {
                      , {{ item.province }}
                    }
                  </span>
                }
              </div>
            }
          </ng-template>
        </p-autocomplete>
        <label for="smart_query">{{ 'inputAddress.smartQueryLabel' | transloco }}</label>
      </p-floatlabel>

      @let location = locationForm.value;

      @if (location && locationForm.disabled) {
        @let formattedText =
          [location.address_line1, location.city, location.state].filter((part) => part).join(', ');

        <div class="flex items-center gap-2 text-white font-bold text-lg">
          <i class="pi pi-map-marker text-2xl"></i>
          <div class="flex flex-col">
            <span> {{ formattedText }}</span>
            @if (location.business_name) {
              <span>({{ location.business_name }})</span>
            }
          </div>
        </div>
      } @else if (!hasFocusedAddressBlock) {
        <small class="dark:text-gray-500 text-xs flex items-center gap-2">
          {{ 'inputAddress.queryHint' | transloco }}
          <i
            class="pi pi-info-circle cursor-help"
            [pTooltip]="'inputAddress.postalCodeTooltip' | transloco"
            tooltipPosition="top"
            [escape]="false"
            [showDelay]="200"
          ></i>
        </small>
      }
      <d2d-error-message [control]="locationSearchForm.get('query')" />
    </div>
  </form>

  @if (
    currentLocationText &&
    locationSearchForm.get('query')?.valid &&
    locationSearchForm.get('query')?.value
  ) {
    <!-- || locationSearchForm.get('query')?.disabled -->
    <div class="flex items-center gap-1">
      <div class="flex items-center gap-2 text-white font-bold text-lg">
        <i class="pi pi-map-marker text-2xl"></i>
        <span>{{ currentLocationText }}</span>
      </div>
      <span class="text-sm text-white">
        (<a
          [href]="googleMapsUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="font-semibold text-white underline underline-offset-2 hover:text-primary-200 hover:decoration-2"
          >{{ 'inputAddress.validateLocationLink' | transloco }}
          <i class="pi pi-external-link !text-xs"></i></a
        >)
      </span>
    </div>
  }

  <!-- Error message for main form -->
  <d2d-error-message [control]="locationForm" />
</div>

@if (isLogged() && isPrimaryAddressField()) {
  <!--
    Fuera del contenedor flex de arriba a propósito: al vivir dentro de ese
    flex (gap-4), un <d2d-add-address-modal> con altura 0 igual sumaba un gap
    extra antes del formulario y desalineaba Country/Postal code frente a la
    columna opuesta.

    @defer: este modal (con PrimeNG Dialog, AutoComplete, Select, etc.) no
    debe ir en el bundle inicial de home solo por vivir aquí: se carga en
    segundo plano apenas el navegador queda libre, lista para cuando el
    usuario haga clic en "Agregar dirección".
  -->
  @defer (on idle) {
    <d2d-add-address-modal #addAddressModal (addressAdded)="onAddressAdded()" />
  }
}
